import { publicApi } from "@api";
import { envConfig } from "@configs/env";
import { OnboardMode, Paths } from "@constants";
import { useLoginSubmit } from "@hooks/useLoginSubmit";
import { useMemberSettings } from "@hooks/useMemberSettings";
import usePreference from "@hooks/usePreference";
import { OnboardingDialogState } from "@types";
import { clearReferralCode, getReferralCode } from "@utils/referral";
import { APIErrorCodes, AuthToken, delay, standardizePhoneNumber, useHandleApiResponse } from "kz-ui-sdk";
import { PropsWithChildren, useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";
import FormOTP from "../FormOTP";
import FormVerifyPIN from "../FormVerifyPIN";

enum LoginSubPage {
  PIN,
  FORGOT_PIN,
  CREATE_NEW,
  OTP,
}

interface FormOTPAndPINProps extends PropsWithChildren {
  phone: string;
  onOTPComplete?: (values: { phone: string; otp: string; pin: string }) => void;
  mode: OnboardMode;
  deferUpdateToken?: boolean;
  onLoginSuccess?: (token?: AuthToken) => void;
  onRegisterSuccess?: () => void;
  onUpdatePINSuccess?: () => void;
  otpMode: "register" | "forgot-password";
}

// referral errors
const REFERRAL_ERROR_CODES: string[] = [
  APIErrorCodes.Member.AlreadyReferral,
  APIErrorCodes.Member.NotFoundReferralCode,
  APIErrorCodes.Member.ChainReferralLoop,
  APIErrorCodes.Member.MaxReferralReached,
  APIErrorCodes.Member.SelfReferralError,
];

export const DEFAULT_OTP_LENGTH = 6;

const FormOTPAndPIN = ({
  phone,
  mode,
  deferUpdateToken = false,
  onLoginSuccess,
  onUpdatePINSuccess,
  onRegisterSuccess,
  otpMode,
}: FormOTPAndPINProps) => {
  const [subPage, setSubPage] = useState<LoginSubPage>(LoginSubPage.OTP);
  const [OTPValue, setOTPValue] = useState("");
  const [errorMsgOTP, setErrorMsgOTP] = useState("");
  const { serverInfo } = usePreference();
  const { phonePrefix } = useMemberSettings();
  const [verifyOTP, { isLoading: isVerifyingOTP }] = publicApi.useVerifyOTPMutation();

  const [submitPIN, { isLoading: isSubmittingPIN }] =
    otpMode === "register" ? publicApi.useSubmitRegisterMutation() : publicApi.useSubmitResetPasswordMutation();

  const { handleApiResponse } = useHandleApiResponse({ toast });

  const { handleLogin, isLoading: isLoginLoading } = useLoginSubmit({
    onLoginSuccess,
    deferUpdateToken,
  });

  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleChangeSubPage = (step: LoginSubPage) => {
    setSubPage(step);
  };

  const handleOnOTPComplete = async (code: string) => {
    setOTPValue(code);
    if (code.length === (serverInfo?.onboard?.otpLength ?? DEFAULT_OTP_LENGTH)) await handleVerifyOTP(code);
  };

  const handleSubmitNewPIN = useCallback(
    async (pin: string) => {
      if (isSubmittingPIN) return;
      // Only send referral code if mode is register
      const referralCode = mode === OnboardMode.REGISTER ? getReferralCode() : undefined;

      const response = await submitPIN({
        phone: standardizePhoneNumber(phone, phonePrefix),
        accountId: envConfig.accountId,
        code: OTPValue,
        pin: pin,
        referralCode,
      });

      handleApiResponse(response, {
        successMessage: t(mode === OnboardMode.REGISTER ? "Register successfully!" : "pin updated successfully"),
        toastError: false,
        onSuccess: () => {
          delay().then(() => {
            handleLogin(phone, pin);
            // Clear referral code upon use
            if (referralCode) {
              clearReferralCode();
            }
            if (mode === OnboardMode.REGISTER) {
              onRegisterSuccess?.();
            } else {
              onUpdatePINSuccess?.();
            }
          });
        },
        onError: (error) => {
          // If referral error, show toast error, clear refCode and redirect back to register page
          if (error?.data?.error?.message && REFERRAL_ERROR_CODES.includes(error?.data?.error?.message)) {
            toast.error(t(error?.data?.error?.message));
            clearReferralCode();
            navigate(generatePath(Paths.PUBLIC.ONBOARD_REGISTER_DIALOG), {
              replace: true,
              state: { openOnboarding: true, mode: OnboardMode.REGISTER } as OnboardingDialogState,
            });
            return;
          }

          setOTPValue("");
          //If PIN is ok but OTP is incorrect, redirect back to OTP form, but keep the PIN
          //User have to re-enter OTP, if correct then log them in immediately
          setSubPage(LoginSubPage.OTP);
          setErrorMsgOTP(error?.data?.error?.message ?? "");
        },
      });
    },
    [
      OTPValue,
      handleApiResponse,
      isSubmittingPIN,
      mode,
      navigate,
      onUpdatePINSuccess,
      phone,
      submitPIN,
      t,
      phonePrefix,
      onRegisterSuccess,
      handleLogin,
    ],
  );

  const handleVerifyOTP = useCallback(
    async (code: string) => {
      const resVerifyOTP = await verifyOTP({
        phone: standardizePhoneNumber(phone, phonePrefix),
        code: code,
        accountId: envConfig.accountId,
      });

      handleApiResponse(resVerifyOTP, {
        toastError: false,
        toastSuccess: false,
        onSuccess: () => {
          handleChangeSubPage(LoginSubPage.PIN);
        },
        onError: (error) => {
          if (error?.data?.error?.message) {
            setErrorMsgOTP(t(error?.data?.error.message));
          }
        },
      });
    },
    [handleApiResponse, phone, t, verifyOTP, phonePrefix],
  );

  const isFormLoading = useMemo(() => {
    return isLoginLoading || isSubmittingPIN || isVerifyingOTP;
  }, [isLoginLoading, isSubmittingPIN, isVerifyingOTP]);

  return (
    <div className="mx-auto w-full max-w-full">
      {subPage === LoginSubPage.OTP && (
        <FormOTP
          OTPValue={OTPValue}
          onComplete={handleOnOTPComplete}
          phone={phone}
          errorMessage={errorMsgOTP}
          mode={otpMode}
        />
      )}
      {subPage === LoginSubPage.PIN && (
        <FormVerifyPIN
          phone={phone}
          loading={isFormLoading}
          mode={mode}
          onSubmit={(values) => {
            handleSubmitNewPIN(values?.pin as string);
          }}
        />
      )}
    </div>
  );
};

export default FormOTPAndPIN;
